import { useTranslations } from "@src/packages/i18n";
import type { PlanAction } from "../plan.types";
import { OrganicMiniIcon } from "./OrganicMiniIcon";
import {
  TimelineActionRow,
  TimelineSubtaskCard,
} from "./TimelineTaskComponents";
import { StepperSection } from "./Stepper";
import MedicalHistoryStepCard from "./MedicalHistoryStepCard";
import { CoverageActionRow } from "./EggFreezingBiologyDeepCarouselCard";
import {
  SpermCarousel,
  type SpermCarouselItem,
} from "./SpermFreezingUnderstandStep";

export function KnowBaselineStep({
  onAction,
}: {
  onAction: (action: PlanAction) => void;
}) {
  const t = useTranslations();
  const testingSlides: SpermCarouselItem[] = [
    {
      id: "semen-analysis",
      label: t("homepage.sperm_freezing_baseline.core_test"),
      title: t("homepage.shared.semen_analysis"),
      body: t("homepage.sperm_freezing_baseline.semen_analysis_body"),
      icon: "sperm",
      cta: {
        label: t("homepage.sperm_freezing_baseline.check_testing"),
        screen: "AtHomeFertilityTesting",
        params: { focus: "semen-analysis" },
      },
    },
    {
      id: "infectious-disease",
      label: t("homepage.sperm_freezing_baseline.often_required"),
      title: t("homepage.sperm_freezing_baseline.infectious_disease_title"),
      body: t("homepage.sperm_freezing_baseline.infectious_disease_body"),
      icon: "panel",
    },
    {
      id: "dna-fragmentation",
      label: t("homepage.sperm_freezing_baseline.sometimes_useful"),
      title: t("homepage.sperm_freezing_baseline.dna_fragmentation_title"),
      body: t("homepage.sperm_freezing_baseline.dna_fragmentation_body"),
      icon: "genetics",
    },
    {
      id: "hormones-urology",
      label: t("homepage.sperm_freezing_baseline.if_abnormal"),
      title: t("homepage.sperm_freezing_baseline.hormones_urology_title"),
      body: t("homepage.sperm_freezing_baseline.hormones_urology_body"),
      icon: "hormones",
    },
  ];

  return (
    <StepperSection
      id="precycle-sperm-freezing.baseline"
      stepLabel={t("homepage.sperm_freezing_baseline.step_label")}
      showRail={false}
    >
      <MedicalHistoryStepCard
        stepNumber={2}
        totalSteps={5}
        onAction={onAction}
        showStepPill={false}
        bottomMargin={0}
      />

      <TimelineSubtaskCard
        id="precycle-sperm-freezing.baseline.tests"
        title={t("homepage.sperm_freezing_baseline.what_to_test")}
        subtitle={t("homepage.sperm_freezing_baseline.what_to_test_subtitle")}
        titleIcon={<OrganicMiniIcon kind="panel" size={34} />}
        collapsedPreviewHeight={68}
        defaultExpanded
      >
        <SpermCarousel items={testingSlides} onAction={onAction} />
      </TimelineSubtaskCard>

      <TimelineActionRow
        title={t("homepage.shared.upload_semen_analysis_results")}
        subtitle={t("homepage.sperm_freezing_baseline.upload_subtitle")}
        icon={<OrganicMiniIcon kind="sperm" size={34} />}
        onPress={() =>
          onAction({
            label: t("homepage.shared.upload_semen_analysis_results"),
            signal: "open-document-upload",
            params: { initialCategory: "medical_records" },
          })
        }
      />

      <CoverageActionRow onAction={onAction} variant="sperm-freezing" />
    </StepperSection>
  );
}
